import { MetaProvider, Title } from "@solidjs/meta";
import { type Location, Router, useLocation } from "@solidjs/router";
import { FileRoutes } from "@solidjs/start/router";
import { stAnalytics } from "@storytell/analytics";
import "./app.css";
import { subscribeToGlobalEvent } from "@storytell/events";
import { QueryClient, QueryClientProvider } from "@tanstack/solid-query";
import { injectSpeedInsights } from "@vercel/speed-insights";
import { createEffect, on, onCleanup, onMount, Suspense } from "solid-js";
import { RootSuspenseFallback } from "~/components/loading/RootSuspenseFallback";
import { useTheme } from "~/hooks/useTheme";
import { I18nProvider } from "~/i18n/context";
import "~/i18n/debug";
import { imageUrlCache } from "~/lib/imageUrlCache";
import { screenForPath } from "~/lib/screenForPath";
import { ErrorBoundary } from "./components/error";

const queryClient = new QueryClient();

// The script is served by Vercel's edge; any other host answers its request with the app's
// HTML 404.
if (VERCEL_ENV) injectSpeedInsights();

/**
 * App constructs the application root.
 *
 * Route groups own their wrapping:
 * - `(marketing)/` → wire-free static shell (landing, legal, blog)
 * - `(app)/` → WireProvider + AppLayout + global modals
 *
 * Server routes (`api/`, `og/`, `sitemap.xml.ts`) live at the root and don't go through either layout.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <MetaProvider>
        {/* Default document title; per-route <Title> overrides it. Owns the
            title so entry-server.tsx no longer emits a competing first one. */}
        <Title>Chief</Title>
        <Router
          root={(props) => (
            // Without a fallback, a lazy route chunk that fails to load or a
            // resource that suspends past the ClientSideSplashScreen's 500ms
            // timer leaves the user staring at a blank page. A stalled chunk
            // never errors, so RootSuspenseFallback auto-reloads once to recover.
            <Suspense fallback={<RootSuspenseFallback />}>
              <QueryClientProvider client={queryClient}>
                <I18nProvider>
                  {props.children}
                  <PageViewTracker />
                  <ThemeRouteSync />
                  <SignOutStorageCleanup />
                </I18nProvider>
              </QueryClientProvider>
            </Suspense>
          )}
        >
          <FileRoutes />
        </Router>
      </MetaProvider>
    </ErrorBoundary>
  );
}

const PageViewTracker = () => {
  const location = useLocation();
  // Keyed on the path alone: the side panel lives in the query string, and opening it isn't a
  // page change.
  createEffect(
    on(
      () => location.pathname,
      (pathname) => {
        void stAnalytics.page({ screen: screenForPath(pathname) });

        window.dataLayer?.push({
          event: "pageview",
          page: window.location.href,
        });

        const adgroup = parseUrlForAdgroup(location);
        if (adgroup) {
          stAnalytics.plugins.posthog.setPersonProperties({ adgroup: adgroup });
          void stAnalytics.track("adgroup_campaign", { adgroup_event: adgroup });
        }
      }
    )
  );
  return undefined;
};

const ThemeRouteSync = () => {
  const location = useLocation();
  const { followRoute } = useTheme();
  // Deferred: the server already painted the first page's theme.
  createEffect(on(() => location.pathname, followRoute, { defer: true }));
  return undefined;
};

const parseUrlForAdgroup = (location: Location) => {
  if (!location.query.adgroup) return;
  return location.query.adgroup as string;
};

// Scanned by prefix because the keys are scoped per user and per project, which this hook
// can't enumerate. Bump the version where the picker writes these too.
const MENTION_RECENTS_KEY_PREFIX = "mention_recents:v1:";

// Clears per-session storage on sign-out so nothing leaks across users sharing a browser: signed
// image URLs, and the mention recents (which hold Google contact emails).
const SignOutStorageCleanup = () => {
  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("sign-out", () => {
      imageUrlCache.clear();
      try {
        const stale = Object.keys(localStorage).filter((key) => key.startsWith(MENTION_RECENTS_KEY_PREFIX));
        for (const key of stale) localStorage.removeItem(key);
      } catch {
        // Best-effort; an unavailable localStorage must not block sign-out.
      }
    });
    onCleanup(unsubscribe);
  });
  return undefined;
};
